import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const inspirationImages = [
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/dbe7534cbbd0a22ddb8e58c2d63836f2.jpeg',
    alt: 'Allée de mariage',
    category: 'Événementiel',
  },
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/f77c5d1e38fb14144dbefad5ae4929ea.jpeg',
    alt: "Hall d'Entrée",
    category: 'Intérieur',
  },
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/77439906415ee6c0b7449726f7990fa0.jpeg',
    alt: 'Figurine couple mariage',
    category: 'Création 3D',
  },
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/cc48c6313703945e04cfe503229fd76d.jpeg',
    alt: 'Anniversaire adulte prestige',
    category: 'Événementiel',
  },
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/4db849ce555041e83c7486d626c8d5ff.jpeg',
    alt: 'Rendu 3D chambre',
    category: 'Création 3D',
  },
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/19a0cc40440e8fe06cf33e96142b4063.jpeg',
    alt: 'Espace détente bureau',
    category: 'Intérieur',
  },
  {
    src: 'https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/c1787ccf192a21ac8bc2dfb8f0cd3a18.jpeg',
    alt: 'Gala entreprise',
    category: 'Événementiel',
  },
];

const categoryColors: Record<string, string> = {
  Événementiel: 'bg-brand-fuchsia/80',
  Intérieur: 'bg-brand-coral/80',
  'Création 3D': 'bg-brand-gold/80',
};

const InspirationsGallery = () => {
  const { ref, isVisible } = useScrollAnimation();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <section className="bg-brand-beige py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-6 bg-brand-coral" />
            <span className="text-brand-coral text-xs font-medium tracking-[0.2em] uppercase">
              Nos inspirations
            </span>
            <span className="h-px w-6 bg-brand-coral" />
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-brand-anthracite mb-4">
            Un regard sur nos inspirations
          </h2>
          <p className="text-brand-gray max-w-lg mx-auto text-base leading-relaxed">
            Un aperçu de quelques inspirations — chaque image communique une énergie et inspire une histoire.
          </p>
        </div>

        <div
          ref={ref}
          className={`columns-2 md:columns-3 gap-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          {inspirationImages.map((img, idx) => (
            <div
              key={img.alt}
              className="break-inside-avoid mb-4 relative overflow-hidden rounded-xl cursor-pointer group"
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3">
                <span
                  className={`text-white text-xs font-medium px-2.5 py-1 rounded-full ${categoryColors[img.category]}`}
                >
                  {img.category}
                </span>
              </div>
              <div
                className={`absolute inset-0 transition-all duration-300 flex items-end p-4 ${hoveredIdx === idx ? 'opacity-100' : 'opacity-0'}`}
                style={{
                  background: 'linear-gradient(to top, rgba(230,62,139,0.7), transparent)',
                }}
              >
                <p className="text-white text-sm font-medium">{img.alt}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const RealisationsPlaceholder = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-white py-24 px-6">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="h-px w-6 bg-brand-coral" />
          <span className="text-brand-coral text-xs font-medium tracking-[0.2em] uppercase">
            Nos réalisations
          </span>
          <span className="h-px w-6 bg-brand-coral" />
        </div>
        <h2 className="font-serif text-4xl lg:text-5xl text-brand-anthracite mb-5">
          À venir prochainement
        </h2>
        <p className="text-brand-gray text-base leading-relaxed max-w-xl mx-auto">
          Cette section sera bientôt enrichie avec nos réalisations concrètes. Restez connectés pour découvrir les projets que nous avons eu le plaisir de mener à bien.
        </p>
      </div>
    </section>
  );
};

const GalleryHero = () => (
  <section className="relative h-[420px] md:h-[520px] flex items-end overflow-hidden">
    <img
      src="https://readdy.ai/api/search-image?query=beautiful%20curated%20interior%20design%20gallery%20wall%20elegant%20artistic%20photographs%20framed%20artwork%20warm%20lighting%20sophisticated%20living%20space%20Abidjan%20Africa%20luxury%20decoration%20moodboard%20aesthetic&width=1920&height=520&seq=evco_galleryhero001&orientation=landscape"
      alt="Galerie Ev & Co Design"
      className="absolute inset-0 w-full h-full object-cover object-top"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-brand-anthracite/80 via-brand-anthracite/30 to-transparent" />
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
      <div className="inline-flex items-center gap-2 mb-3">
        <span className="h-px w-6 bg-brand-gold" />
        <span className="text-brand-gold text-xs font-medium tracking-[0.2em] uppercase">
          Galerie
        </span>
      </div>
      <h1 className="font-serif text-4xl lg:text-6xl text-white leading-tight">
        Portfolio
      </h1>
      <p className="text-white/70 mt-3 max-w-lg text-base">
        Découvrez les inspirations et réalisations qui définissent notre univers créatif.
      </p>
    </div>
  </section>
);

const GalleryPage = () => (
  <>
    <title>Galerie & Portfolio | Ev & Co Design Abidjan</title>
    <meta
      name="description"
      content="Découvrez les inspirations et réalisations de l'agence Ev & Co Design à Abidjan. Galerie d'images événementiel, intérieur et création 3D."
    />
    <meta name="keywords" content="galerie décoration Abidjan, portfolio Ev Co Design, inspirations design Côte d'Ivoire" />
    <link rel="canonical" href="https://evcodesign-ci.com/galerie" />
    <meta property="og:title" content="Galerie & Portfolio | Ev & Co Design Abidjan" />
    <meta property="og:description" content="Inspirations et réalisations de l'agence Ev & Co Design à Abidjan." />
    <main className="font-sans bg-brand-beige min-h-screen">
      <Navbar />
      <GalleryHero />
      <InspirationsGallery />
      <RealisationsPlaceholder />
      <Footer />
    </main>
  </>
);

export default GalleryPage;